import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CreditCard, Banknote, Smartphone, Plus, Trash2, Loader2, CheckCircle2 } from "lucide-react";

/**
 * POSPayment - Componentes de pagamento do POS
 */

// Métodos de pagamento disponíveis
const PAYMENT_METHODS = [
  { id: "card", label: "Cartão", icon: CreditCard, color: "bg-blue-500" },
  { id: "cash", label: "Dinheiro", icon: Banknote, color: "bg-emerald-500" },
  { id: "bizum", label: "Bizum", icon: Smartphone, color: "bg-purple-500" },
  { id: "mbway", label: "MBWay", icon: Smartphone, color: "bg-red-500" },
];

/**
 * CashChangeDialog - Diálogo para pagamento em dinheiro com cálculo de troco
 */
export function CashChangeDialog({
  open,
  onClose,
  total,
  onConfirm,
  processing = false
}) {
  const [needsChange, setNeedsChange] = useState(false);
  const [received, setReceived] = useState("");
  const change = parseFloat(received) - total;
  const isValid = !needsChange || parseFloat(received) >= total;

  // Valores rápidos de notas
  const quickAmounts = [5, 10, 20, 50, 100].filter(a => a >= Math.floor(total));

  const handleConfirm = () => {
    if (!isValid) return;
    if (needsChange) {
      onConfirm(parseFloat(received), change > 0 ? change : 0);
    } else {
      onConfirm(total, 0); // pagamento exato
    }
  };

  const handleToggle = () => {
    setNeedsChange(v => !v);
    setReceived("");
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-center">Pagamento em Dinheiro</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Total */}
          <div className="bg-gray-100 rounded-xl p-4 text-center">
            <p className="text-sm text-gray-500">Total a pagar</p>
            <p className="text-3xl font-bold text-gray-800">{total.toFixed(2)}€</p>
          </div>

          {/* Toggle: precisa de troco? */}
          <button
            type="button"
            onClick={handleToggle}
            className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 font-semibold transition ${
              needsChange
                ? "border-amber-400 bg-amber-50 text-amber-800"
                : "border-emerald-400 bg-emerald-50 text-emerald-700"
            }`}
            data-testid="toggle-needs-change"
          >
            <span className="text-sm">{needsChange ? "Precisa de troco" : "Pagamento exato (sem troco)"}</span>
            <span className={`w-11 h-6 rounded-full flex items-center transition-colors px-0.5 ${needsChange ? "bg-amber-400" : "bg-emerald-400"}`}>
              <span className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${needsChange ? "translate-x-5" : "translate-x-0"}`} />
            </span>
          </button>

          {/* Input valor recebido — só quando precisa troco */}
          {needsChange && (
            <>
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Valor entregue pelo cliente</label>
                <Input
                  type="number"
                  step="0.01"
                  value={received}
                  onChange={(e) => setReceived(e.target.value)}
                  placeholder={`ex: ${Math.ceil(total / 5) * 5}.00`}
                  className="text-2xl text-center h-14 font-bold"
                  autoFocus
                  data-testid="cash-received-input"
                />
              </div>

              {/* Notas rápidas */}
              {quickAmounts.length > 0 && (
                <div className="flex gap-2 flex-wrap justify-center">
                  {quickAmounts.map(amount => (
                    <Button
                      key={amount}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setReceived(amount.toString())}
                      className="min-w-[60px]"
                    >
                      {amount}€
                    </Button>
                  ))}
                </div>
              )}

              {/* Troco calculado */}
              {parseFloat(received) > 0 && (
                <div className={`rounded-xl p-4 text-center ${change >= 0 ? "bg-emerald-50" : "bg-red-50"}`}>
                  <p className="text-sm text-gray-500">Troco</p>
                  <p className={`text-3xl font-bold ${change >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                    {change >= 0 ? `${change.toFixed(2)}€` : `Faltam ${Math.abs(change).toFixed(2)}€`}
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={handleConfirm}
            disabled={!isValid || processing}
            className="bg-emerald-500 hover:bg-emerald-600"
            data-testid="confirm-cash-payment"
          >
            {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * CombinedPaymentDialog - Diálogo para pagamentos combinados/divididos
 */
export function CombinedPaymentDialog({
  open,
  onClose,
  total,
  onConfirm,
  processing = false
}) {
  const [payments, setPayments] = useState([]);
  const [currentMethod, setCurrentMethod] = useState("card");
  const [currentAmount, setCurrentAmount] = useState("");

  const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
  const remaining = total - totalPaid;

  const addPayment = () => {
    const amount = parseFloat(currentAmount);
    if (amount > 0 && amount <= remaining + 0.01) {
      setPayments([...payments, { method: currentMethod, amount }]);
      setCurrentAmount("");
    }
  };

  const removePayment = (index) => {
    setPayments(payments.filter((_, i) => i !== index));
  };

  const handleConfirm = () => {
    if (remaining <= 0.01) {
      onConfirm(payments);
    }
  };

  const getMethodLabel = (method) => {
    return PAYMENT_METHODS.find(m => m.id === method)?.label || method;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center">💳 Pagamento Combinado</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Resumo */}
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="bg-gray-100 rounded-lg p-2">
              <p className="text-xs text-gray-500">Total</p>
              <p className="font-bold text-gray-800">{total.toFixed(2)}€</p>
            </div>
            <div className="bg-emerald-50 rounded-lg p-2">
              <p className="text-xs text-gray-500">Pago</p>
              <p className="font-bold text-emerald-600">{totalPaid.toFixed(2)}€</p>
            </div>
            <div className={`rounded-lg p-2 ${remaining > 0.01 ? "bg-amber-50" : "bg-emerald-100"}`}>
              <p className="text-xs text-gray-500">Restante</p>
              <p className={`font-bold ${remaining > 0.01 ? "text-amber-600" : "text-emerald-600"}`}>
                {remaining > 0 ? remaining.toFixed(2) : "0.00"}€
              </p>
            </div>
          </div>

          {/* Lista de pagamentos */}
          {payments.length > 0 && (
            <div className="space-y-2">
              {payments.map((p, idx) => (
                <div key={idx} className="flex items-center justify-between bg-gray-50 rounded-lg p-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{getMethodLabel(p.method)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold">{p.amount.toFixed(2)}€</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-red-400 hover:text-red-600"
                      onClick={() => removePayment(idx)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Adicionar pagamento */}
          {remaining > 0.01 && (
            <div className="border rounded-lg p-3 space-y-3">
              <p className="text-sm font-medium text-gray-600">Adicionar pagamento</p>
              
              {/* Método */}
              <div className="flex gap-2">
                {PAYMENT_METHODS.map(method => (
                  <button
                    key={method.id}
                    onClick={() => setCurrentMethod(method.id)}
                    className={`flex-1 p-2 rounded-lg border-2 text-center transition ${
                      currentMethod === method.id 
                        ? "border-emerald-500 bg-emerald-50" 
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <method.icon className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-xs">{method.label}</span>
                  </button>
                ))}
              </div>

              {/* Valor */}
              <div className="flex gap-2">
                <Input
                  type="number"
                  step="0.01"
                  value={currentAmount}
                  onChange={(e) => setCurrentAmount(e.target.value)}
                  placeholder={`Máx: ${remaining.toFixed(2)}€`}
                  className="flex-1"
                />
                <Button onClick={addPayment} className="bg-emerald-500 hover:bg-emerald-600">
                  <Plus className="w-4 h-4 mr-1" /> Adicionar
                </Button>
              </div>

              {/* Valor restante como atalho */}
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setCurrentAmount(remaining.toFixed(2))}
              >
                Usar valor restante ({remaining.toFixed(2)}€)
              </Button>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={handleConfirm}
            disabled={remaining > 0.01 || processing}
            className="bg-emerald-500 hover:bg-emerald-600"
          >
            {processing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : remaining <= 0.01 ? (
              <>
                <CheckCircle2 className="w-4 h-4 mr-1" /> Finalizar
              </>
            ) : (
              "Adicione pagamentos"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * PaymentMethodSelector - Seletor de método de pagamento
 */
export function PaymentMethodSelector({ selected, onSelect, showCombined = true }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      {PAYMENT_METHODS.map(method => (
        <button
          key={method.id}
          onClick={() => onSelect(method.id)}
          className={`p-3 rounded-xl border-2 text-center transition ${
            selected === method.id
              ? "border-emerald-500 bg-emerald-50"
              : "border-gray-200 hover:border-gray-300"
          }`}
        >
          <method.icon className={`w-6 h-6 mx-auto mb-1 ${selected === method.id ? "text-emerald-600" : "text-gray-500"}`} />
          <span className={`text-sm font-medium ${selected === method.id ? "text-emerald-700" : "text-gray-700"}`}>
            {method.label}
          </span>
        </button>
      ))}
      {showCombined && (
        <button
          onClick={() => onSelect("combined")}
          className={`p-3 rounded-xl border-2 text-center transition ${
            selected === "combined"
              ? "border-emerald-500 bg-emerald-50"
              : "border-gray-200 hover:border-gray-300"
          }`}
        >
          <div className="flex justify-center gap-1 mb-1">
            <CreditCard className="w-4 h-4" />
            <Banknote className="w-4 h-4" />
          </div>
          <span className={`text-sm font-medium ${selected === "combined" ? "text-emerald-700" : "text-gray-700"}`}>
            Combinado
          </span>
        </button>
      )}
    </div>
  );
}
